import PrioridadeBadge from "../Shared/PrioridadeBadge";
import { formatarData, formatarMoeda } from "../../../utils/solicitacoes/formatters";
import { humanizarEnum } from "../../../utils/solicitacoes/statusConfig";
import { processoInfo } from "../../../utils/solicitacoes/processoRegistry";

/**
 * O que cada processo realmente pergunta no formulário — não dados de workflow (etapa, responsável,
 * criado em: isso já aparece no corpo compacto do card). Lido do MESMO `detalhe` que o card já tem
 * em mãos (buscado uma única vez por processo.service.buscarDetalhe) — nenhuma chamada nova é feita
 * ao expandir. `motivo`/`descricao` ficam de fora daqui porque já aparecem como resumo no card.
 */
const CAMPOS_FORMULARIO_POR_PROCESSO = {
    SOLICITACAO_COMPRAS: d => [
        { label: "Filial", valor: d?.filialNome },
        { label: "Tipo de Produto", valor: d?.tipoProdutoNome },
        d?.produtos?.length ? { label: "Itens", valor: `${d.produtos.length} produto${d.produtos.length > 1 ? "s" : ""}` } : null,
        { label: "Fornecedor", valor: d?.fornecedorNome },
        (d?.valorFinalCotacao ?? d?.valorCompra ?? d?.menorCotacao ?? d?.valorTotalEstimado) != null && {
            label: "Valor", valor: formatarMoeda(d.valorFinalCotacao ?? d.valorCompra ?? d.menorCotacao ?? d.valorTotalEstimado),
        },
        d?.prioridade && { label: "Prioridade", valor: <PrioridadeBadge prioridade={d.prioridade} /> },
    ],
    LANCAMENTO_DESPESA: d => [
        { label: "Filial", valor: d?.filialNome },
        { label: "Fornecedor", valor: d?.fornecedorNome },
        { label: "Tipo de Despesa", valor: humanizarEnum(d?.tipoDespesa) },
        d?.valorTotal != null && { label: "Valor Total", valor: formatarMoeda(d.valorTotal) },
        d?.vencimento && { label: "Vencimento", valor: formatarData(d.vencimento) },
        d?.numerosNotasFiscais?.length ? { label: "Nota Fiscal", valor: d.numerosNotasFiscais.join(", ") } : null,
    ],
    PROCESSO_MULTAS: d => [
        { label: "Condutor", valor: d?.condutorNome },
        { label: "Veículo", valor: d?.veiculoPlaca },
        { label: "Natureza", valor: d?.natureza },
        d?.dataInfracao && { label: "Data da Infração", valor: formatarData(d.dataInfracao) },
        d?.valorMulta != null && { label: "Valor da Multa", valor: formatarMoeda(d.valorMulta) },
        d?.dataVencimento && { label: "Vencimento", valor: formatarData(d.dataVencimento) },
    ],
    // Processo único (Promoção/Enquadramento/Transferência) — só mostra os campos do(s) tipo(s)
    // de mudança que a solicitação de fato incluiu (`inclui*`).
    PROMOCAO_FUNCIONARIOS: d => [
        { label: "Funcionário", valor: d?.funcionarioNome },
        (d?.incluiPromocao || d?.incluiEnquadramento) && { label: "Cargo Atual", valor: d?.cargoAtual },
        (d?.incluiPromocao || d?.incluiEnquadramento) && { label: "Cargo Proposto", valor: d?.cargoProposto },
        d?.incluiPromocao && d?.salarioProposto != null && { label: "Salário Proposto", valor: formatarMoeda(d.salarioProposto) },
        d?.incluiPromocao && d?.dataPrevistaMudanca && { label: "Data Prevista", valor: formatarData(d.dataPrevistaMudanca) },
        d?.incluiTransferencia && { label: "Filial de Destino", valor: d?.filialPropostaNome },
        d?.incluiEnquadramento && { label: "Escala Proposta", valor: d?.escalaProposta },
        { label: "Justificativa", valor: d?.justificativa },
    ],
    DESLIGAMENTO_PESSOAL: d => [
        { label: "Funcionário", valor: d?.funcionarioNome },
        { label: "Cargo", valor: d?.cargo },
        { label: "Motivo", valor: humanizarEnum(d?.motivoDesligamento) },
        d?.dataPrevistaDesligamento && { label: "Data Prevista", valor: formatarData(d.dataPrevistaDesligamento) },
        { label: "Tipo de Aviso", valor: humanizarEnum(d?.tipoAviso) },
        d?.pertenceCipa && { label: "CIPA", valor: "Pertence à CIPA" },
    ],
    SOLICITACAO_PESSOAL: d => [
        { label: "Filial", valor: d?.unidadeNome },
        { label: "Motivo", valor: humanizarEnum(d?.motivoVaga) },
        { label: "Cargo a Preencher", valor: d?.cargoAPreencher },
        { label: "Setor", valor: d?.setor },
        d?.vagaSigilosa && { label: "Vaga Sigilosa", valor: "Sim" },
    ],
    ABERTURA_CHAMADO_TI: d => [
        { label: "Resumo", valor: d?.resumo },
        { label: "Tipo de Serviço", valor: d?.tipoServicoNome },
        d?.statusFinalizacao && { label: "Resultado", valor: humanizarEnum(d?.statusFinalizacao) },
    ],
    RECOLHIMENTO_ATIVOS: d => [
        { label: "Cliente", valor: d?.clienteRazaoSocial },
        { label: "Filial", valor: d?.filialNome },
        d?.itens?.length ? { label: "Itens", valor: `${d.itens.length} item${d.itens.length > 1 ? "s" : ""}` } : null,
        d?.dataPrevistaRecolhimento && { label: "Data Prevista", valor: formatarData(d.dataPrevistaRecolhimento) },
    ],
    SOLICITACAO_ATIVOS: d => [
        { label: "Cliente", valor: d?.clienteRazaoSocial },
        { label: "Filial", valor: d?.filialNome },
        d?.itens?.length ? { label: "Itens", valor: `${d.itens.length} item${d.itens.length > 1 ? "s" : ""}` } : null,
        d?.dataPrevistaEntrega && { label: "Entrega Prevista", valor: formatarData(d.dataPrevistaEntrega) },
    ],
    ANALISE_LIMITE_PRAZO: d => [
        { label: "Cliente", valor: d?.clienteRazaoSocial },
        { label: "Tipo", valor: humanizarEnum(d?.tipoSolicitacao) },
        d?.limiteProposto != null && { label: "Limite Proposto", valor: formatarMoeda(d.limiteProposto) },
        d?.prazoProposto != null && { label: "Prazo Proposto", valor: `${d.prazoProposto} dias` },
        { label: "Motivo da Alteração", valor: d?.motivoAlteracao },
    ],
    CADASTRO_CLIENTE: d => [
        { label: "Cliente", valor: d?.nomeCliente || d?.razaoSocial },
        { label: "Tipo", valor: humanizarEnum(d?.tipoSolicitacao) },
        { label: "Filial", valor: d?.filialNome },
        { label: "CPF/CNPJ", valor: d?.cpf || d?.cnpj },
        { label: "Telefone", valor: d?.telefone1 },
        (d?.cidade || d?.estado) && { label: "Cidade/UF", valor: [d.cidade, d.estado].filter(Boolean).join("/") },
    ],
    RENOVACAO_GDM: d => [
        { label: "Cliente", valor: d?.clienteRazaoSocial },
        { label: "Código", valor: d?.clienteCodigo },
        d?.gdm?.numeroSerie && { label: "Nº de Série", valor: d.gdm.numeroSerie },
        d?.gdm?.gdmDescricao && { label: "GDM", valor: d.gdm.gdmDescricao },
        d?.documentosNaoEnviados?.length > 0 && { label: "Docs. não enviados", valor: d.documentosNaoEnviados.map(n => n.documento).join(", ") },
    ],
};

function Campo({ label, valor }) {
    if (valor == null || valor === "") return null;
    return (
        <div className="flex flex-col gap-1 min-w-0 rounded-md bg-muted/40 px-2.5 py-2">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold leading-none">{label}</span>
            <span className="text-[13px] leading-snug text-foreground font-medium break-words line-clamp-3">{valor}</span>
        </div>
    );
}

/**
 * Conteúdo do Popover exibido ao passar o mouse sobre o KanbanCard — foca no que o formulário
 * daquele processo específico coletou.
 */
export default function TaskCardDetalhes({ solicitacao, detalhe }) {
    const processo = processoInfo(solicitacao.tipoProcesso);
    const camposFormulario = (CAMPOS_FORMULARIO_POR_PROCESSO[solicitacao.tipoProcesso]?.(detalhe) || [])
        .filter(Boolean)
        .filter(c => c.valor != null && c.valor !== "");

    return (
        <div className="p-3">
            <div className="flex items-center gap-2 pb-2.5 mb-3 border-b border-border">
                {processo?.icone && <i className={`${processo.icone} text-primary text-sm`} />}
                <span className="text-xs font-semibold text-foreground truncate">
                    {processo?.label || solicitacao.tipoProcesso}
                </span>
                <span className="ml-auto text-[11px] text-muted-foreground font-medium">{solicitacao.numero}</span>
            </div>
            {camposFormulario.length > 0 ? (
                <div className="grid grid-cols-2 gap-2.5">
                    {camposFormulario.map(c => <Campo key={c.label} {...c} />)}
                </div>
            ) : (
                <p className="text-xs text-muted-foreground py-1">Sem dados adicionais do formulário.</p>
            )}
        </div>
    );
}
